import React, { useMemo } from 'react'
import { Box, Chip, Typography } from '@mui/material'

import { AppCrudTable, AppEmptyLine, AppNote, AppTableSkeleton } from '@components'
import AppTableSearch from '@components/@base/Datatable/Search'
import { dsColorNew } from '@palette'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { PUSHES_PAGE } from '../@services/useLastChanges'
import { pickupLabel, unitLine } from '../@services/windowLabel'
import { resolutionStyle } from './resolution'
import { ChangeMoneyCell, ObservedCell, OpenInActivity, PairCell, PassCell, PointsCell, ResolutionChip } from './PushRows'


const PAGE = 30

/**
 * "Pushes of the selected cell" under the map (plan § 4.3): every pass that decided the selected pick-up (every
 * ACRISS, from a date header) or the selected cell, newest first, including the ones that did not change the
 * price; search by pass id or resolution; 30 per page; the footer counts; the chip says the selection. An ACRISS
 * cell opens the cell detail. Its rows are the Excel's Pushes sheet (criterion 16). Amounts follow the page's
 * Total / Per day pill, the changes with their € under the % (phase 2), and the unit is said above the table.
 */
export default function PushesTable({ sel, pushes, view, data, todayIso, onClear, onOpenCell }) {
	const tz = data?.window?.tz
	const lor = data?.sample?.lor || view.lor
	const unit = view.unit
	const rows = useMemo(() => (pushes.items || []).map(p => ({ ...p, searchText: `${p.pass} ${resolutionStyle(p).label} ${p.sipp} ${p.strategy_name || ''}`.toLowerCase() })), [pushes.items])

	const columns = [
		{ name: 'ts', label: 'Pass', options: { filter: false, customBodyRowRender: (_, row) => <PassCell push={row} tz={tz}/> } },
		{ name: 'sipp', label: 'ACRISS', options: { filter: false, customBodyRowRender: (v, row) => <Box component="button" type="button" onClick={() => onOpenCell(row.sipp, row.pickup_date)} title="Open the cell detail"
			sx={{ font: 'inherit', fontWeight: 700, color: dsColorNew.appBlue, background: 'none', border: 0, p: 0, cursor: 'pointer' }}>{v}</Box> } },
		{ name: 'market_before', label: 'Market before → after', options: { filter: false, sort: false, customBodyRowRender: (_, row) => <PairCell before={row.market_before} after={row.market_after} unit={unit} lor={lor}/> } },
		{ name: 'market_change', label: 'Market change', options: { filter: false, customBodyRowRender: (v, row) => <ChangeMoneyCell ratio={v} before={row.market_before} after={row.market_after} unit={unit} lor={lor}/> } },
		{ name: 'own_before', label: 'Your price before → after', options: { filter: false, sort: false, customBodyRowRender: (_, row) => <PairCell before={row.own_before} after={row.own_after} unit={unit} lor={lor}/> } },
		{ name: 'own_observed_after', label: 'Observed next pass', options: { filter: false, sort: false, hint: 'The shelf price the next pass of this cell read on the site. The figures above use the target the engine set.',
			customBodyRowRender: (v, row) => <ObservedCell observed={v} target={row.own_after} unit={unit} lor={lor} simulated={row.sim && row.counted}/> } },
		{ name: 'own_change', label: 'Engine change', options: { filter: false, customBodyRowRender: (v, row) => <ChangeMoneyCell ratio={v} before={row.own_before} after={row.own_after} unit={unit} lor={lor}/> } },
		{ name: 'delta_pt', label: 'Δ vs market', options: { filter: false, customBodyRowRender: v => <PointsCell pt={v}/> } },
		{ name: 'resolution', label: 'Resolution', options: { filter: false, customBodyRowRender: (_, row) => <ResolutionChip push={row}/> } },
		{ name: 'pass', label: 'Activity', options: { filter: false, sort: false, download: false, setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
			customBodyRowRender: (_, row) => <OpenInActivity push={row} view={view}/> } },
		{ name: 'searchText', label: 'Search', options: { display: 'excluded', filter: false, download: false } },
	]
	const SEARCH = columns.length - 1
	const counts = pushes.counts
	const label = sel?.sipp ? `${sel.sipp} · ${pickupLabel(sel.pickup, todayIso)}` : `all ACRISS · ${pickupLabel(sel.pickup, todayIso)}`

	return <Box sx={{ ...setupScopeSx, '& td': { fontSize: 12.5, py: 0.75 }, '& th': { px: 1.5 } }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
			<Chip label={label} onDelete={onClear} sx={{ backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue, fontWeight: 600 }}/>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{unitLine(unit, lor)}</Typography>
			{pushes.total > PUSHES_PAGE && <Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber }}>Showing the newest {PUSHES_PAGE} of {pushes.total} pushes: narrow to one cell to see them all.</Typography>}
		</Box>
		{pushes.loading && <AppTableSkeleton rows={5} columns={8}/>}
		{pushes.error && <AppNote severity="error">{pushes.error}</AppNote>}
		{!pushes.loading && !pushes.error && !rows.length && <AppEmptyLine>No pass decided this selection in the window.</AppEmptyLine>}
		{!!rows.length && <AppCrudTable
			data={rows} columns={columns}
			options={{
				rowsPerPage: PAGE, rowsPerPageOptions: [PAGE], filter: false, exportExcel: false, selectableRows: 'none',
				sortOrder: { name: 'ts', direction: 'desc' },
				searchPlaceholder: 'Search a pass id or a resolution…',
				customSearch: (query, row) => String(row[SEARCH] || '').includes(String(query || '').toLowerCase()),
				customSearchRender: (searchText, search, hide) => <AppTableSearch onSearch={text => search(text)} onHide={hide}/>,
			}}
		/>}
		{!!counts && !!rows.length && <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1 }}>
			{pushes.total} push{pushes.total === 1 ? '' : 'es'} · {counts.own_changed} with a change of yours · {counts.market_changed} with a market change
		</Typography>}
	</Box>
}
